import React, { useState } from "react";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import TextField from "@mui/material/TextField";
import orderBy from "lodash/orderBy";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export default function WarehouseFilterModal({ value, warehouses = [], onSave, onClose }) {
  // Convert single value to array for backward compatibility
  const initialSelectedIds = Array.isArray(value) ? value : (value ? [value] : []);
  const [selectedWarehouseIds, setSelectedWarehouseIds] = useState(initialSelectedIds);
  const [searchTerm, setSearchTerm] = useState("");

  const sortedWarehouses = orderBy(warehouses, w => w.name);
  const filteredWarehouses = searchTerm
    ? sortedWarehouses.filter(w => 
        w.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        w.warehouseId?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : sortedWarehouses;

  const handleToggle = (warehouseId) => {
    setSelectedWarehouseIds(prev => 
      prev.includes(warehouseId)
        ? prev.filter(id => id !== warehouseId)
        : [...prev, warehouseId]
    );
  };

  const handleSelectAll = () => {
    const allIds = filteredWarehouses.map(w => w.id);
    const allSelected = allIds.every(id => selectedWarehouseIds.includes(id));
    
    if (allSelected) {
      // Deselect all filtered warehouses
      setSelectedWarehouseIds(prev => prev.filter(id => !allIds.includes(id)));
    } else {
      // Select all filtered warehouses
      setSelectedWarehouseIds(prev => [...new Set([...prev, ...allIds])]);
    }
  };

  const handleApply = () => {
    onSave(selectedWarehouseIds);
    onClose();
  };

  const handleCancel = () => {
    setSelectedWarehouseIds(initialSelectedIds);
    onClose();
  };

  const allFilteredIds = filteredWarehouses.map(w => w.id);
  const allSelected = allFilteredIds.length > 0 && allFilteredIds.every(id => selectedWarehouseIds.includes(id));
  const indeterminate = allFilteredIds.some(id => selectedWarehouseIds.includes(id)) && !allSelected;

  return (
    <Dialog open onClose={handleCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ position: "relative" }}>
        <Typography fontSize={20} fontWeight={500} color="text.primary" align="center">
          Warehouse Filters
        </Typography>
        <IconButton
          onClick={handleCancel}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <TextField
            fullWidth
            size="small"
            label="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
          />
        </Box>

        <Box sx={{ maxHeight: 300, overflow: "auto" }}>
          {filteredWarehouses.map((warehouse) => {
            const isChecked = selectedWarehouseIds.includes(warehouse.id);
            return (
              <Box
                key={warehouse.id}
                onClick={() => handleToggle(warehouse.id)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  p: 1,
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: "action.hover"
                  }
                }}
              >
                <Checkbox
                  checked={isChecked}
                  icon={icon}
                  checkedIcon={checkedIcon}
                  sx={{ mr: 1 }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {warehouse.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                    {warehouse.warehouseId}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
        
        {selectedWarehouseIds.length > 0 && (
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              {selectedWarehouseIds.length} warehouse{selectedWarehouseIds.length !== 1 ? 's' : ''} selected
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={handleApply}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
